// src/hooks/useNotifications.js
// ── Aspreo Notification System ────────────────────────────────────────────────
// Polls for 5 event types every 60s and fires browser notifications + sound.
// Reads notif_sound_enabled from localStorage — toggled from Profile page.
// Does NOT touch any core data logic — read-only checks only.

import { useEffect, useRef } from 'react';

const STORAGE_KEY   = 'aspreo_notif_sound';
const FIRED_KEY     = 'aspreo_notif_fired';
const CHECK_MS      = 60 * 1000; // check every 60 seconds

// ── Sound enabled helpers ─────────────────────────────────────────────────────
export function isNotifSoundEnabled() {
  try { return localStorage.getItem(STORAGE_KEY) !== 'false'; } catch { return true; }
}
export function setNotifSoundEnabled(val) {
  try { localStorage.setItem(STORAGE_KEY, val ? 'true' : 'false'); } catch {}
}

// ── Fired-set helpers (prevent double-firing same event) ─────────────────────
function getFired() {
  try { return new Set(JSON.parse(sessionStorage.getItem(FIRED_KEY) || '[]')); } catch { return new Set(); }
}
function addFired(key) {
  try {
    const s = getFired(); s.add(key);
    sessionStorage.setItem(FIRED_KEY, JSON.stringify([...s].slice(-100)));
  } catch {}
}

// ── Trigger one notification ──────────────────────────────────────────────────
function fire({ title, body, icon = '/logo192.png', soundType = 'bell', onDing }) {
  // Sound
  if (isNotifSoundEnabled() && onDing) {
    try { onDing(soundType); } catch {}
  }

  // Browser push notification (only if permission granted and page not focused)
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      new Notification(title, { body, icon, badge: '/logo192.png', silent: true });
    } catch {}
  }
}

// ── Main hook ─────────────────────────────────────────────────────────────────
export function useNotifications({ data, uid, onDing, addLocalNotif }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!uid) return;

    const check = () => {
      const fired    = getFired();
      const today    = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const nowMs    = today.getTime();

      // ── 1. Reminders due in next 5 minutes ─────────────────────────────────
      (data.reminders || []).forEach(r => {
        if (r.dismissed) return;
        const dt = r.reminder_datetime || r.datetime;
        if (!dt) return;
        const key = `reminder-${r.id}-${todayStr}`;
        if (fired.has(key)) return;
        const diffMs = new Date(dt).getTime() - nowMs;
        if (diffMs >= -60000 && diffMs <= 5 * 60 * 1000) {
          addFired(key);
          fire({ title: '⏰ Reminder', body: r.text, soundType: 'bell', onDing });
          if (addLocalNotif) addLocalNotif('⏰ Reminder', r.text);
        }
      });

      // ── 2. Due tasks (weekly_tasks where date = today and not done) ─────────
      const dueTasks = (data.tasks || []).filter(t =>
        t.date === todayStr && !t.done
      );
      if (dueTasks.length > 0) {
        const key = `due-tasks-${todayStr}`;
        if (!fired.has(key)) {
          addFired(key);
          const msg = dueTasks.length === 1
            ? `"${dueTasks[0].text}" is due today`
            : `${dueTasks.length} tasks due today`;
          fire({ title: '📋 Tasks Due Today', body: msg, soundType: 'bell', onDing });
          if (addLocalNotif) addLocalNotif('📋 Tasks Due Today', msg);
        }
      }

      // ── 3. Birthdays / anniversaries today ─────────────────────────────────
      const mm = today.getMonth() + 1;
      const dd = today.getDate();
      (data.specialDates || []).forEach(d => {
        if (Number(d.month) !== mm || Number(d.day) !== dd) return;
        const key = `birthday-${d.id || d.name}-${todayStr}`;
        if (fired.has(key)) return;
        addFired(key);
        const isBday = (d.type || 'birthday') === 'birthday';
        const emoji  = isBday ? '🎂' : '💍';
        const type   = isBday ? 'Birthday' : 'Anniversary';
        fire({ title: `${emoji} ${type} Today!`, body: `${d.name}'s ${type.toLowerCase()} is today`, soundType: 'bell', onDing });
        if (addLocalNotif) addLocalNotif(`${emoji} ${type}`, `${d.name}'s ${type.toLowerCase()} is today`);
      });

      // ── 4. Pending fellow requests ─────────────────────────────────────────
      const pendingFellows = (data.fellows || []).filter(
        f => f.status === 'pending' && f.addressee_id === uid
      );
      if (pendingFellows.length > 0) {
        const key = `fellow-requests-${todayStr}-${pendingFellows.length}`;
        if (!fired.has(key)) {
          addFired(key);
          const msg = pendingFellows.length === 1
            ? 'Someone sent you a fellow request'
            : `${pendingFellows.length} pending fellow requests`;
          fire({ title: '👥 Fellow Request', body: msg, soundType: 'bell', onDing });
          if (addLocalNotif) addLocalNotif('👥 Fellow Request', msg);
        }
      }

      // ── 5. Pending project invites ─────────────────────────────────────────
      const pendingInvites = (data.projectMembers || []).filter(
        m => m.status === 'pending' && m.user_id === uid
      );
      if (pendingInvites.length > 0) {
        const key = `project-invites-${todayStr}-${pendingInvites.length}`;
        if (!fired.has(key)) {
          addFired(key);
          const msg = pendingInvites.length === 1
            ? 'You have a pending project invitation'
            : `${pendingInvites.length} pending project invitations`;
          fire({ title: '📁 Project Invite', body: msg, soundType: 'bell', onDing });
          if (addLocalNotif) addLocalNotif('📁 Project Invite', msg);
        }
      }
    };

    // Run immediately then every 60s
    check();
    timerRef.current = setInterval(check, CHECK_MS);
    return () => clearInterval(timerRef.current);

  }, [uid, data.reminders, data.tasks, data.specialDates, data.fellows, data.projectMembers]); // eslint-disable-line react-hooks/exhaustive-deps
}
